import { Box, Slide, type Theme } from "@mui/material";
import { BerlinThemeProvider } from "@src/appV2/redesign/theme/ThemeProvider";
import { type ReactNode, useMemo } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

import { getEmployees } from "../../modules/employees";
import { getLocations } from "../../modules/locations";
import { get, getAdminProfile, logout, pseudo } from "../../modules/session";
import { initFirebaseSession } from "../../modules/session/init";
import { initCognitoSession } from "@src/appV2/Auth/cognito/initCognitoSession";
import { SSO_CALLBACK_PATH } from "@src/appV2/Auth/RipplingSso/paths";
import { FacilityStatusObj } from "@src/appV2/redesign/utils/facility";
import { AppLayout } from "./appLayout";
import { useEnsureCognitoSession } from "@src/appV2/Auth/cognito/useEnsureCognitoSession";
import { useSyncCognitoApiTokenFlag } from "@src/appV2/Auth/cognito/useSyncCognitoApiTokenFlag";
import { useSyncFirebaseSessionFallbackFlag } from "@src/appV2/Auth/cognito/useSyncFirebaseSessionFallbackFlag";
import { KnockTokenProvider, useKnockTokenContext } from "@src/appV2/Knock/context";
import { KnockGuidesBanner } from "@src/appV2/Knock/components/KnockGuidesBanner";
import { KnockGuidesModal } from "@src/appV2/Knock/components/KnockGuidesModal";
import { KnockProviderWrapper } from "@src/appV2/Knock/components/KnockProviderWrapper";
import { ReviewPromptAttributesEmitter } from "@src/appV2/redesign/RatingPrompt/ReviewPromptAttributesEmitter";
import { ReviewPromptGuide } from "@src/appV2/redesign/RatingPrompt/ReviewPromptGuide";
import { useIsRedesignEnabled } from "@src/appV2/redesign/hooks/useIsRedesignEnabled";
import { PushNotificationProvider } from "@src/appV2/redesign/PushNotifications/PushNotificationProvider";
import { NavigationMenuContainer } from "../redesign/AppLayout/NavigationMenuContainer";
import { useInitializeLaunchDarkly } from "@src/hooks/useInitializeLaunchDarkly";
import { Loader } from "@src/appV2/redesign/components/Loader";
import { SplashContent } from "@src/mobile/MobileSplash/SplashContent";
import { Platform } from "@src/mobile/utils/platform";

const mapStateToProps = (state) => ({
  session: state.session,
  hasSession: state.session.userId !== "",
  isAgent: state.session.userId !== "" && state.session.type === "AGENT",
  isAdmin: state.session.userId !== "" && state.session.type === "ADMIN",
  admin: state.session.admin,
  isFacility: state.session.userId !== "" && state.session.type === "FACILITY",
  isSuspended:
    state.session.profile && state.session.profile.status === FacilityStatusObj.SUSPENDED,
  profile: state.session.profile,
  tz: state.session.tz,
  loadingAdminProfile: state.session.loadingAdminProfile,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      logout,
      pseudo,
      get,
      getAdminProfile,
      getLocations,
      getEmployees,
      initFirebaseSession,
      initCognitoSession,
    },
    dispatch
  );

/**
 * Renders guide consumer components only when the Knock JWT is available.
 * `KnockProviderWrapper` skips providers when the token is still loading,
 * so mounting consumers unconditionally would crash with "useGuide must be
 * used within a KnockGuideProvider".
 */
interface KnockGuidesConsumersProps {
  workplaceUserId: string;
  workplaceName?: string;
}

function KnockGuidesConsumers(props: KnockGuidesConsumersProps) {
  const { workplaceUserId, workplaceName } = props;
  const { knockToken } = useKnockTokenContext();
  if (!knockToken) {
    return null;
  }

  return (
    <BerlinThemeProvider>
      <Slide mountOnEnter direction="left" in timeout={{ enter: 280 }}>
        <Box
          sx={(theme: Theme) => ({
            position: "fixed",
            top: theme.spacing(4),
            right: theme.spacing(4),
            zIndex: theme.zIndex.snackbar ?? 1400,
            width: 380,
            maxWidth: `calc(100% - ${theme.spacing(8)})`,
          })}
        >
          <KnockGuidesBanner filter={{ type: "banner" }} />
        </Box>
      </Slide>
      <KnockGuidesModal filter={{ type: "modal" }} />
      <ReviewPromptGuide workplaceId={workplaceUserId} workplaceName={workplaceName} />
      <ReviewPromptAttributesEmitter />
    </BerlinThemeProvider>
  );
}

function LayoutSelector(props) {
  useSyncCognitoApiTokenFlag();
  useSyncFirebaseSessionFallbackFlag();
  useEnsureCognitoSession();

  // Identify the signed-in user with LaunchDarkly BEFORE deciding which
  // layout to render. `useIsRedesignEnabled` (below) reads a flag; if it
  // evaluates before LD has identified the user, it returns the anonymous
  // default (false) and we mount the legacy AppLayout, which does its own
  // workplace start-page redirect to /facility/dashboard with no schedule
  // flag check. By the time LD updates and LayoutSelector re-renders the
  // correct redesigned layout, the user is already on the legacy calendar.
  const { isIdentifiedForCurrentUser } = useInitializeLaunchDarkly({
    profile: props.profile,
    isAdmin: props.isAdmin,
    adminUserId: props.session.adminUserId,
    workplaceId: props.isFacility ? props.session.userId : undefined,
  });

  const useRedesignNav = useIsRedesignEnabled();

  // Only gate when a session exists — unauthenticated users (login, public
  // routes) shouldn't be held on a loader while LD identifies anonymously.
  //
  // Skip the gate on the SSO callback page: `RipplingSsoCallback` captures
  // `?code=...&state=...` from the URL into a `useRef` on first mount, then
  // its own effect strips the params for security. If we replace the tree
  // with `<Loader />` while the session hydrates, the callback unmounts and
  // remounts with an empty URL, fails the "no auth code" branch, and wipes
  // the tokens we just stored. The callback page renders its own loader.
  const isOnSsoCallback = globalThis.location?.pathname === SSO_CALLBACK_PATH;
  if (props.hasSession && !isIdentifiedForCurrentUser && !isOnSsoCallback) {
    // On mobile, use the branded splash so the cold-launch handoff
    // (MobileSplashOverlay fades out → this gate appears) is visually
    // seamless. On web, keep the existing minimal CircularProgress loader.
    return Platform.isMobile ? <SplashContent /> : <Loader minHeight="100vh" />;
  }

  const Layout = useRedesignNav ? NavigationMenuContainer : AppLayout;
  const layoutProps = { ...props, isIdentifiedForCurrentUser };

  // Knock authenticates with the workplace user's id. Mount only for
  // non-admin workplace sessions so impersonation never writes Knock channel
  // data under the wrong user (D-06). Future Knock consumers (web push,
  // guides, engagement tracking) inherit this JWT.
  const workplaceUserId = props.session.user?._id ?? "";
  const knockEnabled = !props.admin && workplaceUserId.length > 0;

  if (knockEnabled) {
    return (
      <KnockTokenProvider userId={workplaceUserId}>
        <KnockGuidesWrapper workplaceUserId={workplaceUserId}>
          <PushNotificationProvider>
            <Layout {...layoutProps} />
          </PushNotificationProvider>
          <KnockGuidesConsumers
            workplaceUserId={workplaceUserId}
            workplaceName={props.profile?.name}
          />
        </KnockGuidesWrapper>
      </KnockTokenProvider>
    );
  }

  return (
    <PushNotificationProvider>
      <Layout {...layoutProps} />
    </PushNotificationProvider>
  );
}

/**
 * Reads the Knock JWT from `KnockTokenProvider` and mounts the full
 * `KnockProvider` + `KnockGuideProvider` tree when the token is available.
 * Must be a child of `KnockTokenProvider`.
 */
function KnockGuidesWrapper(props: { workplaceUserId: string; children: ReactNode }) {
  const { workplaceUserId, children } = props;
  const { knockToken, handleKnockTokenExpiring } = useKnockTokenContext();

  // Snapshot the device's local clock at mount and hold it for the session
  // (D-09). `localHour` / `localDayOfWeek` let Knock dashboard rules gate
  // prompts to reasonable hours (e.g. `localHour >= 9 AND <= 20`) without an
  // engineering deploy; `timezone` is here so region-specific rules are
  // possible later. Held over an interval refresh because the prompting
  // window is hours wide — at worst we miss one mid-session boundary crossing.
  //
  // `useMemo` identity matters: `KnockGuideProvider` re-reads `targetParams`
  // on prop change, so a fresh object literal each render would churn guide
  // evaluations. Stable identity keeps the snapshot truly snapshot-y.
  const targetParamsData = useMemo(() => {
    const now = new Date();
    return {
      platform: Platform.platform,
      isMobileApp: Platform.isMobile,
      localHour: now.getHours(),
      localDayOfWeek: now.getDay(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
  }, []);

  return (
    <KnockProviderWrapper
      userId={workplaceUserId}
      knockToken={knockToken}
      onUserTokenExpiring={handleKnockTokenExpiring}
      targetParams={{ data: targetParamsData }}
    >
      {children}
    </KnockProviderWrapper>
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(LayoutSelector);
